<template>
  <component
    :is="template"
    :class="isModal ? 'checkout-modal' : null"
    :xShort="isLoadingStage"
    :disable-close="isLoadingStage || shouldDisableClose"
    @initialOpen="handleModalOpen"
    @close="handleModalClose"
  >
    <template v-if="isModal" v-slot:before>
      <avatar v-if="!isLoadingStage" :image="app.icon" :altText="app.name" />
    </template>

    <template v-if="isModal" v-slot:after>
      <v-made-with-movement :allow-fixed="false" />
    </template>

    <component
      v-if="shouldRenderStage(STAGES.START)"
      :slot="STAGES.START"
      :is="slotTemplate"
    >
      <div class="p-5 d-flex justify-content-center">
        <v-loader />
      </div>
    </component>

    <component
      v-if="shouldRenderStage(STAGES.PAY)"
      :slot="STAGES.PAY"
      :is="slotTemplate"
    >
      <div class="checkout-modal__content">
        <confirm-purchase
          v-on="$listeners.back ? { back: $listeners.back } : {}"
          :product="product"
          :has-trial="hasTrial"
          @success="handlePurchaseSuccess"
          @error="handlePurchaseError"
        />
      </div>
    </component>

    <component
      v-if="shouldRenderStage(STAGES.CALLBACK)"
      :slot="STAGES.CALLBACK"
      :is="slotTemplate"
    >
      <payment-element-callback
        @success="handlePurchaseSuccess"
        @error="handlePurchaseError"
      />
    </component>

    <component
      v-if="shouldRenderStage(STAGES.SUCCESS)"
      :slot="STAGES.SUCCESS"
      :is="slotTemplate"
    >
      <div class="text-center p-2">
        <v-icon
          icon="done_all"
          size="3rem"
          color="var(--primary-brand-color-hex)"
        />

        <h2 class="mt-2 mb-0">
          {{ $t("components.checkout.checkout.thankYou") }}
        </h2>

        <p class="text-grey-70 mb-4">
          {{ $t("components.checkout.checkout.confirmEmail") }}
        </p>

        <v-button
          v-if="isModal"
          block
          primary
          class="mb-2"
          @click="closeWithoutTracking"
        >
          {{ $t("components.checkout.checkout.continue") }}
        </v-button>
        <v-button block secondary @click="goToReceipt">
          {{ $t("components.checkout.checkout.viewReceipt") }}
        </v-button>
      </div>
    </component>

    <component
      v-if="shouldRenderStage(STAGES.ERROR)"
      :slot="STAGES.ERROR"
      :is="slotTemplate"
    >
      <div class="checkout-modal__content">
        <div class="mt-3">
          <h2 class="mb-2">
            {{ $t("components.checkout.checkout.processingPurchaseProblem") }}
          </h2>
          <p class="lead mt-0 mb-4" v-html="error.message" />
          <template v-if="error.reason === ERROR_REASONS.existing_subscription">
            <v-button primary block @click="() => $router.push('#auth')">
              {{ $t("components.checkout.checkout.errorSignInBtn") }}
            </v-button>
          </template>
          <template v-else>
            <v-button secondary block @click="retry">
              {{ $t("components.checkout.checkout.errorRetryBtn") }}
            </v-button>
          </template>

          <template v-if="isAuthenticated">
            <v-button ghost block class="mt-3" to="/me/account/support">
              {{ $t("components.checkout.checkout.errorAuthnBtn") }}
            </v-button>
          </template>
        </div>
      </div>
    </component>
  </component>
</template>

<script>
import { mapActions, mapGetters } from "vuex"

import { ACTIVITY_TYPES } from "@/store/analytics"

import EventBus, { EVENTS } from "@/lib/event-bus"
import { isBoolean } from "@/lib/utils"

import {
  shouldUseIAP,
  STAGES,
  ERROR_REASONS,
  SETUP_INTENT_PRODUCT_ID
} from "@/lib/checkout-utils"

import Avatar from "@/components/Avatar"

import ConfirmPurchase from "@/components/checkout/ConfirmPurchase"

export default {
  components: {
    Avatar,
    ConfirmPurchase,
    PaymentElementCallback: () =>
      import("@/components/checkout/PaymentElementCallback")
  },

  props: {
    product: { type: Object, required: true },
    isModal: { type: Boolean, default: false }
  },

  data() {
    return {
      error: "",
      stage: STAGES.START,
      isCheckingPurchaseStatus: false,
      STAGES,
      ERROR_REASONS
    }
  },

  computed: {
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters(["app", "appAccess"]),
    ...mapGetters("products", ["hasPastDueSubscriptionForProduct"]),
    ...mapGetters("pages", ["getCurrentPageAccess"]),

    template() {
      return this.isModal ? "v-modal" : "div"
    },

    slotTemplate() {
      return this.isModal ? "template" : "div"
    },

    shouldDisableClose() {
      return (
        !this.appAccess.allow_anonymous_browsing &&
        (this.appAccess.require_sign_in || this.appAccess.is_subscriber_only) &&
        !this.getCurrentPageAccess?.granted &&
        !!this.getCurrentPageAccess?.data?.global_inheritance
      )
    },

    isLoadingStage() {
      return [STAGES.START, STAGES.CALLBACK].includes(this.stage)
    },

    hasTrial() {
      return (
        this.product.purchase_type === "recurring" &&
        (isBoolean(this.product.can_claim_trial)
          ? this.product.can_claim_trial
          : !!this.product.trial_period_days &&
            this.product.trial_period_days > 0) // fallback
      )
    }
  },

  mounted() {
    EventBus.$once(EVENTS.POST_AUTH_FLOW, this.checkPurchaseStatus)
  },

  activated() {
    if (!this.isModal) {
      this.handleModalOpen()
    }
  },

  beforeDestroy() {
    if (!this.isModal) {
      this.track(ACTIVITY_TYPES.CHECKOUT_ABORTED)
    }

    EventBus.$off(EVENTS.POST_AUTH_FLOW, this.checkPurchaseStatus)
  },

  deactivated() {
    this.track(ACTIVITY_TYPES.CHECKOUT_ABORTED)
  },

  methods: {
    ...mapActions("auth", ["addPurchasedProduct"]),
    ...mapActions("analytics", ["trackActivity"]),
    ...mapActions("products", ["hasActivePurchase"]),

    shouldRenderStage(stage) {
      return this.isModal || this.stage === stage
    },

    retry() {
      if (this.product.id === SETUP_INTENT_PRODUCT_ID) {
        this.$router.removeHash()
      } else {
        this.errorMessage = ""
        this.goToStage(STAGES.PAY)
      }
    },

    handleModalOpen() {
      if (this.isCheckingPurchaseStatus) return

      if (
        this.stage !== STAGES.CALLBACK &&
        this.stage !== STAGES.SUCCESS &&
        this.product.id !== SETUP_INTENT_PRODUCT_ID
      ) {
        this.goToStage(STAGES.PAY)
        this.track(ACTIVITY_TYPES.CHECKOUT_STARTED)
      }
    },

    checkPurchaseStatus() {
      if (!this.isAuthenticated) return

      this.isCheckingPurchaseStatus = true

      setTimeout(() => {
        if (this.hasPastDueSubscriptionForProduct(this.product.id)) {
          this.closeWithoutTracking()

          setTimeout(() => {
            EventBus.$emit(EVENTS.TRIGGER_PAST_DUE_PROMPT)
          }, 350)

          return
        }

        this.hasActivePurchase(this.product.id).then(hasPurchase => {
          if (hasPurchase) {
            this.closeWithoutTracking()

            if (!this.isModal) this.handlePurchaseSuccess()
          } else {
            this.goToStage(STAGES.PAY)
          }

          this.isCheckingPurchaseStatus = false
        })
      }, 100)
    },

    closeWithoutTracking() {
      this.$router.removeHash()
      this.handleModalClose(false)
    },

    handlePurchaseSuccess() {
      this.addPurchasedProduct(this.product.id)
      this.$router.removeHash()
      EventBus.$emit(EVENTS.CHECKOUT_COMPLETED, this.product.id)
      this.$emit("success")
    },

    handlePurchaseError(error) {
      this.error = error
      this.goToStage(STAGES.ERROR)
    },

    goToStage(stage) {
      this.$router.push(stage)
    },

    track(activityType) {
      this.trackActivity({
        activityType,
        data: { product: this.product, iap: shouldUseIAP() }
      })
    },

    handleModalClose(trackEvent = true) {
      if (!this.isModal) return

      if (trackEvent && this.stage !== STAGES.SUCCESS) {
        this.track(ACTIVITY_TYPES.CHECKOUT_ABORTED)
      }

      this.$emit("close")
    },

    goToReceipt() {
      if (shouldUseIAP()) {
        const { openIapManager } = require("@/lib/app-store-utils")
        openIapManager()
      } else {
        this.$router.push("/me/account/purchases")
      }
    }
  },

  watch: {
    "$route.hash": {
      immediate: true,
      handler(hash) {
        if (Object.values(STAGES).includes(hash)) {
          this.stage = hash

          if (this.stage === STAGES.PAY) {
            this.checkPurchaseStatus()
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.checkout__pay-wrapper {
  position: relative;
  top: $size--8;
}

.checkout-modal__content {
  position: relative;
  top: -$size--4;
  padding: 0 $size--4;
}
</style>
